import {
	createContext,
	type ReactNode,
	useCallback,
	useContext,
	useEffect,
	useState,
} from "react";

type AdminUser = {
	id: string;
	name: string;
	email: string;
	emailVerified: boolean;
	image: string | null;
	createdAt: string;
	updatedAt: string;
};

type AdminAuthContextValue = {
	adminUser: AdminUser | null;
	loading: boolean;
	checkSession: () => Promise<void>;
	signOut: () => Promise<void>;
};

const AdminAuthContext = createContext<AdminAuthContextValue | null>(null);

export function AdminAuthProvider({ children }: { children: ReactNode }) {
	const [adminUser, setAdminUser] = useState<AdminUser | null>(null);
	const [loading, setLoading] = useState(true);

	const checkSession = useCallback(async () => {
		try {
			const res = await fetch("/api/admin/me", {
				credentials: "include",
			});
			if (!res.ok) {
				setAdminUser(null);
				return;
			}
			const data = (await res.json()) as { user: AdminUser };
			setAdminUser(data.user);
		} catch {
			setAdminUser(null);
		} finally {
			setLoading(false);
		}
	}, []);

	useEffect(() => {
		checkSession();
	}, [checkSession]);

	const signOut = useCallback(async () => {
		try {
			await fetch("/api/admin/auth/sign-out", {
				method: "POST",
				credentials: "include",
				headers: {
					"Content-Type": "application/json",
					Origin: window.location.origin,
				},
				body: JSON.stringify({}),
			});
		} catch {
			// proceed even on network error
		}
		setAdminUser(null);
	}, []);

	return (
		<AdminAuthContext.Provider
			value={{ adminUser, loading, checkSession, signOut }}
		>
			{children}
		</AdminAuthContext.Provider>
	);
}

export function useAdminAuth() {
	const ctx = useContext(AdminAuthContext);
	if (!ctx)
		throw new Error("useAdminAuth must be used within AdminAuthProvider");
	return ctx;
}
